import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import { db } from "../../services/firebase";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import styles from "./ContactUs.module.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import {
  FaUser,
  FaEnvelope,
  FaTag,
  FaRegCommentDots,
  FaClock,
  FaHeartbeat,
  FaStethoscope,
  FaShieldAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AOS from "aos";
import "aos/dist/aos.css";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // Input sanitization for security
    const sanitizedValue = value.replace(
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      ""
    );
    setFormData({ ...formData, [name]: sanitizedValue });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const sanitizedData = {
        name: formData.name.trim().substring(0, 100),
        phone: formData.phone.trim().substring(0, 20),
        email: formData.email.trim().toLowerCase().substring(0, 254),
        subject: formData.subject.trim().substring(0, 200),
        message: formData.message.trim().substring(0, 1000),
      };

      // Basic phone & email validation
      // eslint-disable-next-line no-useless-escape
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,20}$/;
      if (!phoneRegex.test(sanitizedData.phone.replace(/\s/g, ""))) {
        toast.error("Please enter a valid phone number");
        setLoading(false);
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(sanitizedData.email)) {
        toast.error("Please enter a valid email address");
        setLoading(false);
        return;
      }

      const today = new Date();
      const formattedDate = today.toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const formattedName = sanitizedData.name
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");

      // 1️⃣ Send to Admin
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!, // your admin template
        {
          ...sanitizedData,
          createdAt: new Date().toString(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );

      // 2️⃣ Send confirmation to User
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_USER_TEMPLATE_ID!, // your user template
        {
          to_email: sanitizedData.email,
          to_name: formattedName,
          subject: sanitizedData.subject,
          reply_message: sanitizedData.message,
          company_name: "LicoMeds",
          phone: sanitizedData.phone,
          current_date: formattedDate, // custom date from JS
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      );

      // Save to Firestore
      await addDoc(collection(db, "contact-us"), {
        ...sanitizedData,
        createdAt: Timestamp.now(),
        status: "new",
      });

      toast.success(
        "✅ Message sent! A confirmation email has been sent to you."
      );
      setFormData({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (error: any) {
      console.error("Contact form error:", error.message);
      toast.error("❌ Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.contactPage}>
      <Navbar />
      <ScrollToTop />

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroContent} data-aos="fade-up">
            <div className={styles.heroIcon}>
              <FaStethoscope />
            </div>
            <h1 className={styles.heroTitle}>Get in Touch</h1>
            <p className={styles.heroSubtitle}>
              We're here to support your healthcare journey. Reach out to our
              dedicated team for assistance, consultations, or any questions
              about our medical services.
            </p>
            <div
              className={styles.heroStats}
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className={styles.statItem}>
                <FaHeartbeat className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statNumber}>24/7</span>
                  <span className={styles.statLabel}>Medical Support</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <FaClock className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statNumber}>2hrs</span>
                  <span className={styles.statLabel}>Response Time</span>
                </div>
              </div>
              <div className={styles.statItem}>
                <FaShieldAlt className={styles.statIcon} />
                <div className={styles.statText}>
                  <span className={styles.statNumber}>100%</span>
                  <span className={styles.statLabel}>Secure</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ToastContainer position="bottom-left" autoClose={3000} />

      {/* Contact Section */}
      <section className={styles.contactSection}>
        <div className="container">
          <div className="row">
            {/* Contact Form */}
            <div className="col-lg-2"></div>
            <div className="col-lg-8">
              <div
                className={styles.contactCard}
                data-aos="fade-left"
                data-aos-delay="400"
              >
                <div className={styles.formHeader}>
                  <h3 className={styles.formTitle}>Send us a Message</h3>
                  <p className={styles.formSubtitle}>
                    Fill out the form below and our medical support team will
                    get back to you as soon as possible.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className={styles.contactForm}>
                  <div className="row">
                    <div className="col-md-6">
                      <div
                        className={styles.inputGroup}
                        data-aos="fade-up"
                        data-aos-delay="500"
                      >
                        <FaUser className={styles.inputIcon} />
                        <input
                          type="text"
                          className={styles.input}
                          name="name"
                          placeholder="Full Name"
                          value={formData.name}
                          onChange={handleChange}
                          maxLength={100}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div
                        className={styles.inputGroup}
                        data-aos="fade-up"
                        data-aos-delay="600"
                      >
                        <span className={styles.inputIcon}>
                          <i className="fa fa-phone"></i>
                        </span>
                        <input
                          type="tel"
                          className={styles.input}
                          name="phone"
                          placeholder="Phone Number"
                          value={formData.phone}
                          onChange={handleChange}
                          maxLength={20}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div
                        className={styles.inputGroup}
                        data-aos="fade-up"
                        data-aos-delay="700"
                      >
                        <FaEnvelope className={styles.inputIcon} />
                        <input
                          type="email"
                          className={styles.input}
                          name="email"
                          placeholder="Email Address"
                          value={formData.email}
                          onChange={handleChange}
                          maxLength={254}
                        />
                        <span className={styles.optionalLabel}>Optional</span>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div
                        className={styles.inputGroup}
                        data-aos="fade-up"
                        data-aos-delay="800"
                      >
                        <FaTag className={styles.inputIcon} />
                        <input
                          type="text"
                          className={styles.input}
                          name="subject"
                          placeholder="Subject"
                          value={formData.subject}
                          onChange={handleChange}
                          maxLength={200}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    className={styles.inputGroup}
                    data-aos="fade-up"
                    data-aos-delay="900"
                  >
                    <FaRegCommentDots className={styles.inputIcon} />
                    <textarea
                      className={styles.textarea}
                      name="message"
                      placeholder="Tell us how we can help you..."
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={1000}
                      required
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={loading}
                    data-aos="fade-up"
                    data-aos-delay="1000"
                  >
                    {loading ? (
                      <>
                        <span className={styles.spinner}></span>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <FaEnvelope />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
            <div className="col-lg-2"></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
